import { useState, useMemo, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Brain, RefreshCw, Trophy, ChevronRight, Sparkles, Target } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { ProgressBar } from '@/components/ui/ProgressBar';
import { questions, species, shuffleArray, getSpeciesById } from '@/data';
import { useProgress } from '@/hooks/useProgress';
import { useSound } from '@/hooks/useSound';
import {
  difficultyLabels,
  difficultyDescriptions,
  categoryLabels,
  encouragingMessages,
  type Difficulty,
  type QuestionCategory,
} from '@/types';

type QuizState = 'setup' | 'playing' | 'answered' | 'results';

export function Quiz() {
  const [searchParams] = useSearchParams();
  const { progress, recordAnswer, updateStreak, updateSettings } = useProgress();
  const { playCorrect, playWrong, playCelebration } = useSound();

  const [quizState, setQuizState] = useState<QuizState>('setup');
  const [difficulty, setDifficulty] = useState<Difficulty>(progress.settings.selectedDifficulty);
  const [category, setCategory] = useState<QuestionCategory | 'all'>('all');
  const [quizQuestions, setQuizQuestions] = useState<typeof questions>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [correctCount, setCorrectCount] = useState(0);
  const [incorrectCount, setIncorrectCount] = useState(0);
  const [streak, setStreak] = useState(0);
  const [showFeedbackOverlay, setShowFeedbackOverlay] = useState(false);

  // Check for species filter in URL
  const speciesFilter = searchParams.get('species');
  const filteredSpecies = speciesFilter ? getSpeciesById(speciesFilter) : null;

  const currentQuestion = quizQuestions[currentIndex];
  const isCorrect = selectedAnswer === currentQuestion?.correctIndex;

  // Get random encouraging message
  const encouragingMessage = useMemo(
    () => encouragingMessages[Math.floor(Math.random() * encouragingMessages.length)],
    [selectedAnswer]
  );

  const startQuiz = () => {
    let filtered = [...questions];

    // Filter by difficulty
    filtered = filtered.filter((q) => q.difficulty === difficulty);

    // Filter by category
    if (category !== 'all') {
      filtered = filtered.filter((q) => q.category === category);
    }

    // Filter by species if specified
    if (speciesFilter) {
      filtered = filtered.filter((q) => q.relatedSpecies?.includes(speciesFilter));
    }

    // Shuffle and take 10 questions (or all if less than 10)
    const shuffled = shuffleArray(filtered).slice(0, 10);

    if (shuffled.length === 0) {
      // No questions available for this filter
      alert('No questions available for these filters. Try different options!');
      return;
    }

    setQuizQuestions(shuffled);
    setCurrentIndex(0);
    setSelectedAnswer(null);
    setCorrectCount(0);
    setIncorrectCount(0);
    setStreak(0);
    setQuizState('playing');

    // Update streak
    updateStreak();

    // Save difficulty preference
    updateSettings({ selectedDifficulty: difficulty });
  };

  const handleAnswer = (answerIndex: number) => {
    if (selectedAnswer !== null) return;

    setSelectedAnswer(answerIndex);
    const correct = answerIndex === currentQuestion.correctIndex;

    if (correct) {
      setCorrectCount((c) => c + 1);
      setStreak((s) => s + 1);
      playCorrect();
    } else {
      setIncorrectCount((c) => c + 1);
      setStreak(0);
      playWrong();
    }

    // Record answer
    const xpReward = difficulty === 'pup' ? 5 : difficulty === 'raft-member' ? 10 : difficulty === 'alpha-otter' ? 15 : 25;
    recordAnswer(currentQuestion.id, correct, xpReward);

    setQuizState('answered');

    // Show feedback overlay after a short delay so user can see their selection color
    setTimeout(() => {
      setShowFeedbackOverlay(true);
    }, 400);
  };

  const handleNext = () => {
    setShowFeedbackOverlay(false);
    if (currentIndex < quizQuestions.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setSelectedAnswer(null);
      setQuizState('playing');
    } else {
      // Quiz finished
      if (correctCount >= 7) {
        playCelebration();
      }
      setQuizState('results');
    }
  };

  const resetQuiz = () => {
    setQuizState('setup');
    setQuizQuestions([]);
    setCurrentIndex(0);
    setSelectedAnswer(null);
    setCorrectCount(0);
    setIncorrectCount(0);
    setStreak(0);
  };

  // Setup Screen
  if (quizState === 'setup') {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="max-w-lg mx-auto px-4 py-6"
      >
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-full bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center mx-auto mb-4">
            <Brain className="w-8 h-8 text-purple-600 dark:text-purple-400" />
          </div>
          <h1 className="text-2xl font-display font-bold text-slate-800 dark:text-white mb-2">
            Otter Quiz
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            {filteredSpecies
              ? `Test your ${filteredSpecies.commonName} knowledge!`
              : 'Test your knowledge about otters!'}
          </p>
        </div>

        {/* Difficulty Selection */}
        <Card className="mb-4">
          <h2 className="font-display font-bold text-slate-800 dark:text-white mb-3">
            Choose Your Level
          </h2>
          <div className="space-y-2">
            {(Object.keys(difficultyLabels) as Difficulty[]).map((d) => (
              <button
                key={d}
                onClick={() => setDifficulty(d)}
                className={`w-full p-3 rounded-xl text-left transition-all ${
                  difficulty === d
                    ? 'bg-purple-100 dark:bg-purple-900/50 border-2 border-purple-500'
                    : 'bg-slate-50 dark:bg-slate-800 border-2 border-transparent hover:border-slate-200 dark:hover:border-slate-700'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-slate-800 dark:text-white">
                      {difficultyLabels[d]}
                    </p>
                    <p className="text-sm text-slate-500">{difficultyDescriptions[d]}</p>
                  </div>
                  <Badge variant="difficulty" difficulty={d}>
                    {d === 'pup' && '🐾'}
                    {d === 'raft-member' && '🦦'}
                    {d === 'alpha-otter' && '👑'}
                    {d === 'otter-professor' && '🎓'}
                  </Badge>
                </div>
              </button>
            ))}
          </div>
        </Card>

        {/* Category Selection (only if not species-filtered) */}
        {!speciesFilter && (
          <Card className="mb-4">
            <h2 className="font-display font-bold text-slate-800 dark:text-white mb-3">
              Category (Optional)
            </h2>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setCategory('all')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  category === 'all'
                    ? 'bg-purple-500 text-white'
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                }`}
              >
                All Topics
              </button>
              {(Object.keys(categoryLabels) as QuestionCategory[]).map((c) => (
                <button
                  key={c}
                  onClick={() => setCategory(c)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    category === c
                      ? 'bg-purple-500 text-white'
                      : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                  }`}
                >
                  {categoryLabels[c]}
                </button>
              ))}
            </div>
          </Card>
        )}

        <Button onClick={startQuiz} className="w-full" size="lg">
          Start Quiz
          <ChevronRight className="w-5 h-5" />
        </Button>
      </motion.div>
    );
  }

  // Results Screen
  if (quizState === 'results') {
    const percentage = Math.round((correctCount / quizQuestions.length) * 100);
    const message =
      percentage === 100
        ? "Perfect! You're an otter genius! 🎉"
        : percentage >= 80
        ? "Amazing! You really know your otters! 🦦"
        : percentage >= 60
        ? "Great job! Keep learning! 📚"
        : percentage >= 40
        ? "Good effort! Every otter expert started somewhere! 💪"
        : "Keep practicing! You're learning with every question! 🌟";

    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-lg mx-auto px-4 py-6"
      >
        <Card className="text-center">
          <motion.div
            animate={{ rotate: [0, -10, 10, -10, 0] }}
            transition={{ duration: 0.5 }}
            className="text-6xl mb-4"
          >
            {percentage >= 80 ? '🏆' : percentage >= 60 ? '⭐' : '🦦'}
          </motion.div>

          <h1 className="text-2xl font-display font-bold text-slate-800 dark:text-white mb-2">
            Quiz Complete!
          </h1>

          <p className="text-slate-600 dark:text-slate-400 mb-4">{message}</p>

          <div className="flex justify-center gap-8 mb-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-green-600">{correctCount}</p>
              <p className="text-sm text-slate-500">Correct</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-red-500">{incorrectCount}</p>
              <p className="text-sm text-slate-500">Incorrect</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-purple-600">{percentage}%</p>
              <p className="text-sm text-slate-500">Score</p>
            </div>
          </div>

          <ProgressBar value={percentage} color="success" size="lg" />

          <div className="flex gap-3 mt-6">
            <Button variant="secondary" onClick={resetQuiz} className="flex-1">
              <RefreshCw className="w-4 h-4" />
              New Quiz
            </Button>
            <Link to="/achievements" className="flex-1">
              <Button variant="primary" className="w-full">
                <Trophy className="w-4 h-4" />
                View Trophies
              </Button>
            </Link>
          </div>
        </Card>
      </motion.div>
    );
  }

  // Playing/Answered Screen
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="max-w-lg mx-auto px-4 py-6"
    >
      {/* Progress Header */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-slate-500">
            Question {currentIndex + 1} of {quizQuestions.length}
          </span>
          <div className="flex items-center gap-2">
            {streak > 1 && (
              <span className="text-sm font-medium text-orange-500">
                🔥 {streak} streak!
              </span>
            )}
            <Badge variant="difficulty" difficulty={difficulty}>
              {difficultyLabels[difficulty]}
            </Badge>
          </div>
        </div>
        <ProgressBar
          value={((currentIndex + 1) / quizQuestions.length) * 100}
          color="primary"
          size="sm"
        />
      </div>

      {/* Question Card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentQuestion.id}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -50 }}
        >
          <Card className="mb-4">
            <div className="flex items-center gap-2 mb-3">
              <Badge>{categoryLabels[currentQuestion.category]}</Badge>
            </div>

            <h2 className="text-lg font-semibold text-slate-800 dark:text-white mb-6">
              {currentQuestion.question}
            </h2>

            {/* Answer Options */}
            <div className="space-y-3">
              {currentQuestion.options.map((option, index) => {
                const isSelected = selectedAnswer === index;
                const isCorrectAnswer = index === currentQuestion.correctIndex;
                const showResult = quizState === 'answered';

                let buttonClass =
                  'w-full p-4 rounded-xl text-left transition-all border-2 ';

                if (showResult) {
                  if (isCorrectAnswer) {
                    buttonClass +=
                      'bg-green-100 dark:bg-green-900/50 border-green-500 text-green-800 dark:text-green-200';
                  } else if (isSelected && !isCorrectAnswer) {
                    buttonClass +=
                      'bg-red-100 dark:bg-red-900/50 border-red-500 text-red-800 dark:text-red-200';
                  } else {
                    buttonClass +=
                      'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-400';
                  }
                } else {
                  buttonClass += isSelected
                    ? 'bg-otter-100 dark:bg-otter-900/50 border-otter-500'
                    : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-otter-300 dark:hover:border-otter-700';
                }

                return (
                  <motion.button
                    key={index}
                    whileTap={{ scale: quizState === 'playing' ? 0.98 : 1 }}
                    onClick={() => handleAnswer(index)}
                    disabled={quizState === 'answered'}
                    className={buttonClass}
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                          showResult && isCorrectAnswer
                            ? 'bg-green-500 text-white'
                            : showResult && isSelected && !isCorrectAnswer
                            ? 'bg-red-500 text-white'
                            : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
                        }`}
                      >
                        {String.fromCharCode(65 + index)}
                      </span>
                      <span className="flex-1">{option}</span>
                      {showResult && isCorrectAnswer && <span>✓</span>}
                      {showResult && isSelected && !isCorrectAnswer && <span>✗</span>}
                    </div>
                  </motion.button>
                );
              })}
            </div>
          </Card>
        </motion.div>
      </AnimatePresence>

      {/* Feedback Overlay */}
      <AnimatePresence>
        {showFeedbackOverlay && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={handleNext}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-6"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className={`w-full max-w-md rounded-2xl p-6 shadow-2xl ${
                isCorrect
                  ? 'bg-green-50 dark:bg-green-900/90 border-2 border-green-300 dark:border-green-700'
                  : 'bg-amber-50 dark:bg-amber-900/90 border-2 border-amber-300 dark:border-amber-700'
              }`}
            >
              <div className="flex items-start gap-4">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                    isCorrect
                      ? 'bg-green-200 dark:bg-green-800'
                      : 'bg-amber-200 dark:bg-amber-800'
                  }`}
                >
                  {isCorrect ? (
                    <Sparkles className="w-6 h-6 text-green-600 dark:text-green-300" />
                  ) : (
                    <Target className="w-6 h-6 text-amber-600 dark:text-amber-300" />
                  )}
                </div>
                <div className="flex-1">
                  <h3
                    className={`font-display font-bold text-lg mb-2 ${
                      isCorrect
                        ? 'text-green-800 dark:text-green-100'
                        : 'text-amber-800 dark:text-amber-100'
                    }`}
                  >
                    {isCorrect ? 'Correct! 🎉' : encouragingMessage}
                  </h3>
                  <p className="text-slate-700 dark:text-slate-300 text-sm leading-relaxed">
                    {currentQuestion.explanation}
                  </p>
                </div>
              </div>

              <Button
                onClick={handleNext}
                className="w-full mt-5"
                size="lg"
                variant={isCorrect ? 'success' : 'primary'}
              >
                {currentIndex < quizQuestions.length - 1 ? 'Next Question' : 'See Results'}
                <ChevronRight className="w-5 h-5" />
              </Button>

              <p className="text-center text-xs text-slate-500 dark:text-slate-400 mt-3">
                Tap anywhere to continue
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
